import { API_BASE_URL } from '../config/api';
import {
  FeedbackThread,
  FeedbackThreadWithMessages,
  FeedbackThreadMessage,
  CreateFeedbackThreadMessageRequest,
  UpdateFeedbackThreadRequest,
  Artist,
} from '../types/artist';

const getAuthToken = (): string | null => {
  return localStorage.getItem('accessToken');
};

const getAuthHeaders = (): HeadersInit => {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
};

export const feedbackThreadApi = {
  /**
   * Get a feedback thread by feedback ID
   */
  async getThread(feedbackId: number): Promise<FeedbackThread> {
    const response = await fetch(
      `${API_BASE_URL}/api/Feedback/${feedbackId}/thread`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch thread: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Get a feedback thread with paginated messages
   */
  async getThreadWithMessages(
    feedbackId: number,
    page: number = 1,
    pageSize: number = 10
  ): Promise<FeedbackThreadWithMessages> {
    const params = new URLSearchParams({
      page: page.toString(),
      pageSize: pageSize.toString(),
    });

    const response = await fetch(
      `${API_BASE_URL}/api/Feedback/${feedbackId}/thread/messages?${params}`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch thread with messages: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Post a new message to a feedback thread
   */
  async postMessage(
    feedbackId: number,
    request: CreateFeedbackThreadMessageRequest
  ): Promise<FeedbackThreadMessage> {
    const response = await fetch(
      `${API_BASE_URL}/api/Feedback/${feedbackId}/thread/messages`,
      {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to post message: ${response.statusText}`);
    }

    return response.json();
  },

  /**
   * Update a feedback thread (close it or reveal identity)
   */
  async updateThread(
    feedbackId: number,
    request: UpdateFeedbackThreadRequest
  ): Promise<void> {
    const response = await fetch(
      `${API_BASE_URL}/api/Feedback/${feedbackId}/thread`,
      {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to update thread: ${response.statusText}`);
    }
  },

  /**
   * Close a feedback thread
   */
  async closeThread(feedbackId: number, currentAnonymous: boolean): Promise<void> {
    return this.updateThread(feedbackId, { closed: true, anonymous: currentAnonymous });
  },

  /**
   * Reveal reviewer identity in a feedback thread
   */
  async revealIdentity(feedbackId: number, currentClosed: boolean): Promise<void> {
    return this.updateThread(feedbackId, { anonymous: false, closed: currentClosed });
  },

  /**
   * Get the reviewer's artist profile for a feedback thread
   */
  async getReviewerArtist(feedbackId: number): Promise<Artist> {
    const response = await fetch(
      `${API_BASE_URL}/api/Feedback/${feedbackId}/thread/reviewer`,
      {
        headers: getAuthHeaders(),
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch reviewer artist: ${response.statusText}`);
    }

    return response.json();
  },
};
